import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import cartHeroImg from "../assets/seller-cart-hero.png";
import growthMoneyImg from "../assets/requirements-hero.png";
import {
  IdCardIcon,
  FileTextIcon,
  StoreIcon,
  CheckIcon,
  ArrowRightIcon,
  AlertCircleIcon,
  ShoppingBagIcon,
  PackageIcon,
  GrowthChartIcon,
  HeadphonesIcon,
  UsersGroupIcon,
  ClockTimerIcon,
  CheckBadgeIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckSquareIcon,
  SquareIcon,
  CameraIcon,
  TagIcon,
  CompassIcon,
  MessageCircleIcon,
  CreditCardIcon,
  RocketIcon,
} from "../components/Icons";

function Requirements() {
  // FAQ accordion state
  const [openFaq, setOpenFaq] = useState(0);

  // Interactive checklist state
  const [checklist, setChecklist] = useState([
    { id: 1, text: "Prepare high-resolution product photos with clear lighting", done: true },
    { id: 2, text: "Draft product titles, descriptions, and size specifications", done: true },
    { id: 3, text: "Decide competitive selling prices and margin goals", done: false },
    { id: 4, text: "Have valid Aadhaar and PAN details ready for verification", done: true },
    { id: 5, text: "Prepare your store name, logo, and pickup address", done: false },
    { id: 6, text: "Review order fulfillment guidelines and shipping schedule", done: false },
  ]);

  const toggleChecklistItem = (id) => {
    setChecklist((prev) =>
      prev.map((item) => (item.id === id ? { ...item, done: !item.done } : item))
    );
  };

  const completedCount = checklist.filter((item) => item.done).length;
  const progressPercent = Math.round((completedCount / checklist.length) * 100);

  const trustBenefits = [
    { Icon: RocketIcon, title: "Simple Onboarding", desc: "Start in 2 minutes" },
    { Icon: IdCardIcon, title: "Minimal Documents", desc: "Aadhaar & PAN ready" },
    { Icon: StoreIcon, title: "Easy Store Setup", desc: "Instant seller presence" },
    { Icon: HeadphonesIcon, title: "Seller Support", desc: "Guidance at every step" },
    { Icon: GrowthChartIcon, title: "Growth Tools", desc: "0% commission options" },
  ];

  const sellerJourneySteps = [
    { num: "01", title: "Create Account", desc: "Sign up with your email, mobile number, and password in seconds.", Icon: RocketIcon },
    { num: "02", title: "Complete Seller Info", desc: "Tell us your business name, category, and preferred store details.", Icon: StoreIcon },
    { num: "03", title: "Submit Documents", desc: "Provide basic identity & PAN verification for genuine merchant status.", Icon: IdCardIcon },
    { num: "04", title: "Application Review", desc: "Our team verifies your merchant details within 24-48 business hours.", Icon: ClockTimerIcon },
    { num: "05", title: "Get Approved", desc: "Receive immediate approval notification and unlock seller tools.", Icon: CheckBadgeIcon },
    { num: "06", title: "Create Your Store", desc: "Set up your storefront with your banner, logo, and contact info.", Icon: ShoppingBagIcon },
    { num: "07", title: "Add Products", desc: "Upload product photos, size options, stock count, and retail prices.", Icon: PackageIcon },
    { num: "08", title: "Start Selling", desc: "Go live across IM Online and receive orders from customers nationwide.", Icon: SparklesIcon },
  ];

  const minimalDocCards = [
    {
      Icon: IdCardIcon,
      tag: "IDENTITY VERIFICATION",
      title: "Aadhaar Card",
      desc: "Used to verify your identity during seller onboarding and ensure an authentic, trusted merchant ecosystem on IM Online.",
      bullet: "Government-issued identity proof",
    },
    {
      Icon: FileTextIcon,
      tag: "BUSINESS COMPLIANCE",
      title: "PAN Card",
      desc: "Used for individual or business identity verification and statutory financial record requirements for marketplace payouts.",
      bullet: "Individual or Business PAN",
    },
    {
      Icon: StoreIcon,
      tag: "STORE & FULFILLMENT",
      title: "Basic Seller Information",
      desc: "Your legal name, active mobile number, verified email address, dispatch/pickup address, and store display name.",
      bullet: "Contact & address details",
    },
  ];

  const growthPoints = [
    { step: "01", title: "Start Simple", desc: "Create your seller account and launch your online store with zero upfront fees." },
    { step: "02", title: "List Your Products", desc: "Add your footwear and fashion collections with high-resolution photos and pricing." },
    { step: "03", title: "Get Discovered", desc: "Reach thousands of customers actively shopping for quality footwear across India." },
    { step: "04", title: "Learn & Optimize", desc: "Track sales performance, customer preferences, and store analytics in real time." },
    { step: "05", title: "Build Your Business", desc: "Scale your revenue with 0% commission plans and reliable customer reach." },
  ];

  const whySellFeatures = [
    {
      Icon: UsersGroupIcon,
      title: "Reach More Customers",
      desc: "Put your catalog in front of high-intent shoppers searching for trendy sneakers, formal shoes, and apparel.",
    },
    {
      Icon: StoreIcon,
      title: "Your Own Storefront",
      desc: "Build a branded merchant profile with your own logo, banner, and unique store identity.",
    },
    {
      Icon: PackageIcon,
      title: "Easy Product Management",
      desc: "Intuitive portal to add color variants, manage sizes, update prices, and track live inventory.",
    },
    {
      Icon: ShoppingBagIcon,
      title: "Order Management",
      desc: "Simplified order tracking from new orders to fulfillment, dispatch, and delivery updates.",
    },
    {
      Icon: CompassIcon,
      title: "Seller Guidance",
      desc: "Clear tutorials and documentation to help first-time sellers get started without any confusion.",
    },
    {
      Icon: HeadphonesIcon,
      title: "Dedicated Support",
      desc: "Direct access to our seller support team for quick assistance on onboarding and technical queries.",
    },
  ];

  const timelineSteps = [
    { num: "1", title: "Application Submitted", desc: "You submit your basic business profile and contact details." },
    { num: "2", title: "Admin Review", desc: "Our merchant operations team reviews your information." },
    { num: "3", title: "Document Verification", desc: "Quick verification of basic merchant identity." },
    { num: "4", title: "Seller Approval", desc: "Your account is activated with full seller portal access." },
    { num: "5", title: "Store Setup", desc: "Configure your store branding and fulfillment address." },
    { num: "6", title: "Start Selling", desc: "Publish your first product and start receiving customer orders." },
  ];

  const whileYouWaitCards = [
    {
      Icon: CameraIcon,
      title: "Prepare Product Photos",
      desc: "Take bright, high-resolution pictures of your shoes and products against clean white or neutral backdrops.",
    },
    {
      Icon: TagIcon,
      title: "Prepare Product Information",
      desc: "Draft clear product titles, accurate size charts (UK/India sizing), material specs, and color options.",
    },
    {
      Icon: CreditCardIcon,
      title: "Plan Your Pricing",
      desc: "Calculate your production costs, competitor prices, and retail margin to set attractive prices.",
    },
    {
      Icon: StoreIcon,
      title: "Prepare Your Store Profile",
      desc: "Choose an appealing store name, write a friendly brand tagline, and have your logo graphic ready.",
    },
    {
      Icon: CompassIcon,
      title: "Learn How Selling Works",
      desc: "Familiarize yourself with packaging requirements, dispatch timelines, and customer communication.",
    },
  ];

  const pricingPlans = [
    {
      name: "BASIC",
      price: "₹0",
      period: "forever",
      products: "5 products",
      commission: "10% marketplace commission",
      highlight: false,
      badge: "FREE STARTER",
      cta: "Start Free",
      features: ["5 Live Product Listings", "10% Marketplace Commission", "Standard Merchant Store", "Basic Seller Dashboard", "Email Support"],
    },
    {
      name: "GROWTH",
      price: "₹300",
      period: "per month",
      products: "20 products",
      commission: "5% marketplace commission",
      highlight: true,
      badge: "MOST POPULAR",
      cta: "Choose Growth",
      features: ["20 Live Product Listings", "5% Low Marketplace Commission", "Enhanced Store Presence", "Advanced Analytics", "Priority Support"],
    },
    {
      name: "PRO",
      price: "₹499",
      period: "per month",
      products: "50 products",
      commission: "0% Commission (Keep 100%)",
      highlight: false,
      badge: "BEST VALUE",
      cta: "Choose Pro",
      features: ["50 Live Product Listings", "0% Marketplace Commission", "Featured Store Placement", "Full Sales & Trend Insights", "Dedicated Account Manager"],
    },
  ];

  const faqs = [
    {
      q: "Who can sell on IM Online?",
      a: "Any manufacturer, brand owner, wholesaler, or retailer based in India can sell on IM Online. Whether you are an individual shoe artisan or an established retail business, our platform is built to help you reach online customers.",
    },
    {
      q: "What documents do I need to get started?",
      a: "To start seller onboarding, you only need your valid Aadhaar Card, PAN Card, active Indian mobile number, email address, and pickup location address. We keep initial requirements minimal so you can begin quickly.",
    },
    {
      q: "Do I need a GST registration to start?",
      a: "GST requirements depend on your business category, annual turnover, and applicable regulations. GST registration may not be required for every seller to begin the initial onboarding process. Our onboarding team will guide you through the requirements applicable to your business.",
    },
    {
      q: "How much does it cost to sell on IM Online?",
      a: "Starting is completely free with our Basic Plan (₹0/month) with zero upfront fees. We also offer Growth (₹300/mo) and Pro (₹499/mo with 0% marketplace commission) plans when you want to list more products and scale your business.",
    },
    {
      q: "How many products can I list?",
      a: "On the Basic Plan, you can list up to 5 active products. The Growth Plan allows up to 20 products, and the Pro Plan allows up to 50 active product listings.",
    },
    {
      q: "What is the marketplace commission rate?",
      a: "Commission is 10% on the Basic Plan, 5% on the Growth Plan, and 0% on the Pro Plan. On the Pro Plan, you keep 100% of your product selling price!",
    },
    {
      q: "How long does seller verification take?",
      a: "Our team typically verifies and approves seller applications within 24 to 48 business hours. You can check your application status anytime by signing in to the Seller Center.",
    },
    {
      q: "What happens after my application is approved?",
      a: "Once approved, your seller portal is activated. You can set up your store profile, upload product photos and pricing, and start receiving customer orders immediately.",
    },
    {
      q: "How do I create and customize my store?",
      a: "After approval, navigate to the 'My Store' section in your Seller Dashboard. You can upload your store logo, banner image, add a business description, and customize your store profile.",
    },
    {
      q: "How do I add and manage my products?",
      a: "From your Seller Dashboard, simply click 'Add New Product'. Enter your product title, category, price, size variants, stock count, and upload up to 10 product photos.",
    },
    {
      q: "How do I receive and fulfill customer orders?",
      a: "When a customer purchases your product, you receive an instant notification in your Seller Center dashboard. You pack the product, print the shipping slip, and hand it over to our courier partner.",
    },
    {
      q: "How do I contact seller support?",
      a: "You can reach our dedicated seller support team anytime by emailing support@imonline.shop or via the Support section inside your seller portal.",
    },
  ];

  return (
    <>
      <Navbar />

      <div className="req-page-root">
        <SEO
          title="Seller Requirements & Document Checklist | IM Online Seller Portal"
          description="Everything you need to start selling footwear and fashion on IM Online. Minimal document requirements: Aadhaar Card, PAN Card, active bank account, and pickup address."
          keywords="ecommerce seller requirements india, documents needed to sell online, footwear seller criteria, aadhaar pan seller registration, start selling shoes online"
          canonical="https://vendor.imonline.shop/vendor/requirements"
          ogTitle="IM Online Seller Requirements & Verification Guide"
          ogDescription="Minimal documentation, 24-48 hr fast verification, and transparent 0% commission plans. Check requirements to start selling on IM Online."
          schema={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((f) => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": f.a
              }
            }))
          }}
        />
        {/* ═══════════════════════ 1. HERO SECTION ═══════════════════════ */}
        <section className="req-hero-section">
          <div className="req-container">
            <div className="req-hero-grid">
              {/* Left Column: Copy & CTAs */}
              <div className="req-hero-left">
                <span className="req-pill-badge">
                  IM ONLINE SELLER CENTER
                </span>

                <h1 className="req-hero-headline">
                  Start Selling on<br />
                  <span className="req-headline-accent">IM Online.</span>
                </h1>

                <p className="req-hero-subtext">
                  Bring your products to thousands of shoppers with a simple seller platform designed to help you start, manage, and grow your online business with confidence.
                </p>

                <div className="req-hero-ctas">
                  <Link to="/vendor/register" className="req-btn-primary">
                    Start Selling Free <ArrowRightIcon size={18} />
                  </Link>
                  <a href="#seller-journey" className="req-btn-secondary">
                    How It Works
                    <span className="req-play-circle">
                      <ChevronRightIcon size={14} color="#a85050" />
                    </span>
                  </a>
                </div>

                {/* Supporting bullet points */}
                <div className="req-hero-trust-bullets">
                  <div className="req-trust-bullet-item">
                    <span className="req-bullet-icon"><CheckIcon size={14} color="#16a34a" /></span>
                    <span>Simple 2-minute onboarding</span>
                  </div>
                  <div className="req-trust-bullet-item">
                    <span className="req-bullet-icon"><CheckIcon size={14} color="#16a34a" /></span>
                    <span>Minimal documentation</span>
                  </div>
                  <div className="req-trust-bullet-item">
                    <span className="req-bullet-icon"><CheckIcon size={14} color="#16a34a" /></span>
                    <span>0% commission options</span>
                  </div>
                </div>
              </div>

              {/* Right Column: Hero Shopping Cart Image */}
              <div className="req-hero-right">
                <div className="req-hero-visual-stage">
                  <div className="req-hero-blob-backdrop" />
                  <div className="req-hero-dots-pattern" />

                  {/* Clean white frame around shopping cart hero image */}
                  <div className="req-hero-image-frame">
                    <img
                      src={cartHeroImg}
                      alt="Entrepreneur with shopping cart representing online marketplace selling on IM Online"
                      className="req-hero-cart-img"
                    />

                    {/* Floating pill: Bottom left */}
                    <div className="req-hero-float-badge req-float-bottom">
                      <div className="req-float-badge-icon">
                        <CheckIcon size={14} color="#ffffff" />
                      </div>
                      <div className="req-float-badge-text">
                        <strong>Store Ready in Minutes</strong>
                        <span>Simple seller onboarding</span>
                      </div>
                    </div>

                    {/* Floating pill: Top right */}
                    <div className="req-hero-float-badge req-float-top">
                      <div className="req-float-badge-icon req-badge-purple">
                        <StoreIcon size={16} color="#ffffff" />
                      </div>
                      <div className="req-float-badge-text">
                        <strong>5,000+ Indian Sellers</strong>
                        <span>Growing marketplace</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ 2. QUICK TRUST BENEFITS ═══════════════════ */}
        <section className="req-trust-strip-section">
          <div className="req-container">
            <div className="req-trust-benefits-bar">
              {trustBenefits.map((item, idx) => (
                <div key={idx} className="req-trust-benefit-col">
                  <div className="req-trust-benefit-icon">
                    <item.Icon size={22} color="#a85050" />
                  </div>
                  <div className="req-trust-benefit-info">
                    <strong className="req-trust-benefit-title">{item.title}</strong>
                    <span className="req-trust-benefit-desc">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════════════ 3. SIMPLE SELLER JOURNEY ═════════════════ */}
        <section className="req-journey-section" id="seller-journey">
          <div className="req-container">
            <div className="req-section-header-center">
              <span className="req-pill-badge-subtle">SIMPLE SELLER JOURNEY</span>
              <h2 className="req-section-headline">
                From Your First Step to <span className="req-headline-accent">Your First Sale</span>
              </h2>
              <p className="req-section-subtext">
                We've organized your path into clear, straightforward steps so you always know exactly what comes next.
              </p>
            </div>

            <div className="req-journey-grid">
              {sellerJourneySteps.map((step, idx) => (
                <div key={idx} className="req-journey-card">
                  <div className="req-journey-card-top">
                    <span className="req-journey-num">{step.num}</span>
                    <div className="req-journey-icon-wrap">
                      <step.Icon size={22} color="#a85050" />
                    </div>
                  </div>
                  <h3 className="req-journey-title">{step.title}</h3>
                  <p className="req-journey-desc">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════ 4. MINIMAL DOCUMENTS ══════════════════ */}
        <section className="req-documents-section">
          <div className="req-container">
            <div className="req-section-header-center">
              <span className="req-pill-badge-subtle">CLEAR &amp; SIMPLE</span>
              <h2 className="req-section-headline">
                Start With <span className="req-headline-accent">Just What You Need</span>
              </h2>
              <p className="req-section-subtext">
                We only ask for essential verification details to keep onboarding fast and accessible for every seller.
              </p>
            </div>

            <div className="req-doc-cards-row">
              {minimalDocCards.map((card, idx) => (
                <div key={idx} className="req-doc-card">
                  <div className="req-doc-card-header">
                    <div className="req-doc-icon-box">
                      <card.Icon size={26} color="#a85050" />
                    </div>
                    <span className="req-doc-tag">{card.tag}</span>
                  </div>
                  <h3 className="req-doc-title">{card.title}</h3>
                  <p className="req-doc-desc">{card.desc}</p>
                  <div className="req-doc-footer">
                    <span className="req-doc-check-icon"><CheckIcon size={14} color="#16a34a" /></span>
                    <span className="req-doc-bullet-text">{card.bullet}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════ 5. GST INFORMATION ═══════════════════ */}
        <section className="req-gst-section">
          <div className="req-container">
            <div className="req-gst-panel">
              <div className="req-gst-left">
                <div className="req-gst-badge-box">
                  <span className="req-gst-question-mark">?</span>
                </div>
                <div className="req-gst-headings">
                  <span className="req-gst-tag">TAX &amp; COMPLIANCE GUIDANCE</span>
                  <h3 className="req-gst-title">What About GST?</h3>
                </div>
              </div>

              <div className="req-gst-right">
                <p className="req-gst-desc">
                  GST requirements can depend on your business, products, turnover and applicable regulations. GST registration may not be required for every seller to begin the onboarding process. We'll guide you through the requirements applicable to your business.
                </p>

                <div className="req-gst-advisory-box">
                  <AlertCircleIcon size={20} color="#d97706" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Onboarding Guidance:</strong> You can create your account and submit your initial seller application right away. Our team provides category-specific guidance as your store grows.
                  </div>
                </div>

                <div className="req-gst-action-row">
                  <Link to="/vendor/register" className="req-btn-primary">
                    Start Seller Registration <ArrowRightIcon size={16} />
                  </Link>
                  <a href="mailto:support@imonline.shop?subject=GST Query" className="req-btn-outline-brand">
                    <MessageCircleIcon size={16} /> Ask a Question
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════ 6. BUSINESS & GROWTH (MONEY IMAGE) ════════════ */}
        <section className="req-growth-section">
          <div className="req-container">
            <div className="req-growth-grid">
              {/* Left Column: Growth Businessman Image */}
              <div className="req-growth-left">
                <div className="req-growth-image-frame">
                  <div className="req-growth-blob-bg" />
                  <img
                    src={growthMoneyImg}
                    alt="Seller managing his business finances and growing online revenue with IM Online"
                    className="req-growth-img"
                  />
                  <div className="req-growth-floating-card">
                    <GrowthChartIcon size={22} color="#16a34a" />
                    <div>
                      <strong>Transparent Earnings</strong>
                      <span>Direct bank settlements &amp; 0% commission plans</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Growth Story & 5 Steps */}
              <div className="req-growth-right">
                <span className="req-pill-badge-subtle">BUSINESS &amp; INCOME GROWTH</span>
                <h2 className="req-growth-headline">
                  We Don't Just Help You Sell.<br />
                  <span className="req-headline-accent">We Help You Grow.</span>
                </h2>
                <p className="req-growth-subtext">
                  Starting an online footwear business shouldn't mean losing 30% to marketplace commissions or fighting confusing algorithms. IM Online gives you transparent tools, doorstep courier pickups, and dedicated WhatsApp guidance so you can grow with confidence.
                </p>
                <div style={{ marginBottom: "22px" }}>
                  <Link to="/vendor#our-agenda" className="btn-outline-primary" style={{ padding: "8px 18px", fontSize: "13px", fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <span>🌟 Read Our Seller Agenda &amp; Guarantees</span>
                    <span>&rarr;</span>
                  </Link>
                </div>

                <div className="req-growth-steps-list">
                  {growthPoints.map((item, idx) => (
                    <div key={idx} className="req-growth-step-item">
                      <div className="req-growth-step-badge">{item.step}</div>
                      <div className="req-growth-step-text">
                        <strong className="req-growth-step-title">{item.title}</strong>
                        <p className="req-growth-step-desc">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════ 7. WHY SELL WITH IM ONLINE ══════════════ */}
        <section className="req-why-section">
          <div className="req-container">
            <div className="req-section-header-center">
              <span className="req-pill-badge-subtle">BUILT FOR SELLERS</span>
              <h2 className="req-section-headline">
                Everything You Need to <span className="req-headline-accent">Start Selling</span>
              </h2>
              <p className="req-section-subtext">
                A purpose-built merchant suite designed for easy operations, accurate inventory, and nationwide customer reach.
              </p>
            </div>

            <div className="req-why-grid">
              {whySellFeatures.map((feat, idx) => (
                <div key={idx} className="req-why-card">
                  <div className="req-why-icon-square">
                    <feat.Icon size={24} color="#a85050" />
                  </div>
                  <h3 className="req-why-card-title">{feat.title}</h3>
                  <p className="req-why-card-desc">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════ 8. WHAT HAPPENS AFTER I APPLY ════════════ */}
        <section className="req-after-apply-section">
          <div className="req-container">
            <div className="req-section-header-center">
              <span className="req-pill-badge-subtle">TRANSPARENT PROCESS</span>
              <h2 className="req-section-headline">
                What Happens <span className="req-headline-accent">After You Apply?</span>
              </h2>
              <p className="req-section-subtext">
                No guessing or waiting in the dark. Here is our step-by-step review and launch timeline.
              </p>
            </div>

            <div className="req-timeline-cards-row">
              {timelineSteps.map((step, idx) => (
                <div key={idx} className="req-timeline-card">
                  <div className="req-timeline-step-badge">{step.num}</div>
                  <h4 className="req-timeline-title">{step.title}</h4>
                  <p className="req-timeline-desc">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════ 9. WHILE YOU WAIT ════════════════ */}
        <section className="req-wait-prep-section">
          <div className="req-container">
            <div className="req-section-header-center">
              <span className="req-pill-badge-subtle">PROACTIVE PREPARATION</span>
              <h2 className="req-section-headline">
                Get Ready While We <span className="req-headline-accent">Review Your Application</span>
              </h2>
              <p className="req-section-subtext">
                Make your pending review period productive with these 5 quick preparation tasks.
              </p>
            </div>

            <div className="req-wait-cards-grid">
              {whileYouWaitCards.map((card, idx) => (
                <div key={idx} className="req-wait-card">
                  <div className="req-wait-icon-box">
                    <card.Icon size={24} color="#a85050" />
                  </div>
                  <h3 className="req-wait-title">{card.title}</h3>
                  <p className="req-wait-desc">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════ 10. INTERACTIVE CHECKLIST ════════════ */}
        <section className="req-checklist-section">
          <div className="req-container">
            <div className="req-checklist-interactive-card">
              <div className="req-checklist-header">
                <div>
                  <span className="req-pill-badge-subtle">INTERACTIVE CHECKLIST</span>
                  <h3 className="req-checklist-main-title">Your Seller Starter Checklist</h3>
                  <p className="req-checklist-sub">
                    Click items as you get them ready to track your progress toward launch day!
                  </p>
                </div>

                {/* Live Progress Indicator */}
                <div className="req-checklist-progress-pill">
                  <div className="req-progress-count">
                    <strong>{completedCount}</strong> of <strong>{checklist.length}</strong> Completed
                  </div>
                  <div className="req-progress-bar-bg">
                    <div className="req-progress-bar-fill" style={{ width: `${progressPercent}%` }} />
                  </div>
                </div>
              </div>

              <div className="req-checklist-items-grid">
                {checklist.map((item) => (
                  <div
                    key={item.id}
                    className={`req-check-item-row ${item.done ? "completed" : ""}`}
                    onClick={() => toggleChecklistItem(item.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleChecklistItem(item.id); }}
                  >
                    <div className="req-check-box-icon">
                      {item.done ? (
                        <CheckSquareIcon size={22} color="#16a34a" />
                      ) : (
                        <SquareIcon size={22} color="#888682" />
                      )}
                    </div>
                    <span className="req-check-item-text">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════ 11. SELLER PLANS ════════════════ */}
        <section className="req-pricing-preview-section">
          <div className="req-container">
            <div className="req-section-header-center">
              <span className="req-pill-badge-subtle">TRANSPARENT PRICING</span>
              <h2 className="req-section-headline">
                Choose How You <span className="req-headline-accent">Want to Grow</span>
              </h2>
              <p className="req-section-subtext">
                Start completely free with zero subscription fees, or scale up with 0% commission plans.
              </p>
            </div>

            <div className="req-pricing-cards-grid">
              {pricingPlans.map((plan, idx) => (
                <div key={idx} className={`req-pricing-card ${plan.highlight ? "highlighted" : ""}`}>
                  <div className="req-plan-header">
                    <span className="req-plan-badge">{plan.badge}</span>
                    <h3 className="req-plan-name">{plan.name}</h3>
                    <div className="req-plan-price-wrap">
                      <span className="req-plan-price">{plan.price}</span>
                      <span className="req-plan-period">/{plan.period}</span>
                    </div>
                    <p className="req-plan-tagline">{plan.products} • {plan.commission}</p>
                  </div>

                  <ul className="req-plan-features-list">
                    {plan.features.map((feat, fIdx) => (
                      <li key={fIdx}>
                        <CheckIcon size={16} color="#16a34a" className="flex-shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/vendor/register"
                    className={plan.highlight ? "req-btn-primary w-100" : "req-btn-outline-brand w-100"}
                    style={{ justifyContent: "center", textAlign: "center" }}
                  >
                    {plan.cta} <ArrowRightIcon size={16} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════ 12. FAQ ACCORDION ════════════════════ */}
        <section className="req-faq-section">
          <div className="req-container">
            <div className="req-section-header-center">
              <span className="req-pill-badge-subtle">FREQUENTLY ASKED QUESTIONS</span>
              <h2 className="req-section-headline">
                Got Questions? <span className="req-headline-accent">We've Got Answers.</span>
              </h2>
              <p className="req-section-subtext">
                Everything you need to know about seller onboarding, requirements, fees, and operations.
              </p>
            </div>

            <div className="req-faq-accordion-wrap">
              {faqs.map((faq, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div key={idx} className={`req-faq-item ${isOpen ? "open" : ""}`}>
                    <button
                      type="button"
                      className="req-faq-question-btn"
                      onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                      aria-expanded={isOpen}
                    >
                      <span>{faq.q}</span>
                      <span className="req-faq-chevron">
                        <ChevronDownIcon size={18} color="#a85050" />
                      </span>
                    </button>
                    {isOpen && (
                      <div className="req-faq-answer-body">
                        <p>{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ════════════════ 13. FINAL CALL TO ACTION ════════════════ */}
        <section className="req-final-cta-section">
          <div className="req-container">
            <div className="req-final-cta-banner">
              <div className="req-final-cta-content">
                <span className="req-final-pill">READY TO LAUNCH?</span>
                <h2 className="req-final-headline">
                  Your Products Deserve<br />More Customers.
                </h2>
                <p className="req-final-subtext">
                  Take the first step toward building and scaling your online footwear and fashion business with IM Online.
                </p>

                <div className="req-final-actions">
                  <Link to="/vendor/register" className="req-btn-white-solid">
                    Start Selling Free <ArrowRightIcon size={18} />
                  </Link>
                  <Link to="/vendor/login" className="req-btn-ghost-white">
                    Sign In to Portal
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default Requirements;

